import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { Back, gsap } from "gsap";
import PixiPlugin from "gsap/PixiPlugin";
import { AbstractGameView } from "./AbstractGameView";
gsap.registerPlugin(PixiPlugin);
/**
 * AbstractSlingoAnimation.ts
 */
export class AbstractSlingoAnimation extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    create() {
        super.create();
        this.createFields();
        //this.createAnimation();
        this.reset();
    }
    postCreate() {
        super.postCreate();
        Logger.addObjectRef(this, "SlingoAnimation");
    }
    createFields() {
        this.fields = new Dictionary();
        for (let total of this.uiProperties.amountFields.getKeys()) {
            let propertiesL = this.landscapeProperties.amountFields.getValue(total);
            let propertiesP = this.portraitProperties?.amountFields?.getValue(total);
            let field = ViewUtils.createField(this, propertiesL, propertiesP);
            field.pivot.set(0, field.getHeight() / 2);
            this.fields.setValue(total, field);
        }
    }
    reset() {
        this.hasAmount = false;
        for (let field of this.fields.getValues()) {
            field.alpha = 1;
            field.visible = false;
        }
        this.activeField = null;
    }
    createAnimation() {
        // to be overriden
    }
    playAnimation(numberOfSlingos) {
        // to be overriden
    }
    play(numberOfSlingos) {
        this.playAnimation(numberOfSlingos);
        this.addTextAnimation(numberOfSlingos);
    }
    addTextAnimation(numberOfSlingos) {
        if (numberOfSlingos > 1) {
            this.hasAmount = true;
            this.activeField = this.fields.getValue(numberOfSlingos);
            this.activeField.alpha = 0;
            this.activeField.scale.set(0, 0);
            this.activeField.visible = true;
            gsap.timeline()
                .to(this.activeField, { alpha: 1, duration: 0.4 }, 0)
                .to(this.activeField, { pixi: { scaleX: 1.15, scaleY: 1 }, duration: 0.5, ease: Back.easeOut }, 0)
                .to(this.activeField, { pixi: { scaleX: 1, scaleY: 1 }, duration: 0.25, ease: Back.easeOut }, 0.5);
        }
    }
    handleBackgroundComplete() {
        if (this.hasAmount) {
            gsap.to(this.activeField, {
                alpha: 0,
                duration: 0.15,
                pixi: {
                    scaleX: 0,
                    scaleY: 0
                },
                ease: Back.easeOut,
                onComplete: this.handleComplete,
                callbackScope: this,
            });
        }
        else {
            this.handleComplete();
        }
    }
    handleComplete() {
        gsap.killTweensOf(this.activeField);
        this.reset();
        this.callHandler(this.completeHandler, this);
    }
    onComplete(handler, scope) {
        this.completeHandler = new EventHandler(handler, scope);
    }
}
/**
 * SlingoAnimationSprite.ts
 */
export class SlingoAnimationSprite extends AbstractSlingoAnimation {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    createAnimation() {
        this.landscapeAnimation = this.assetManager.getMovieClip(this.landscapeProperties.animationProperties);
        this.landscapeAnimation.onComplete = this.handleBackgroundComplete.bind(this);
        this.addChild(this.landscapeAnimation);
        if (this.hasPortraitProperties) {
            this.portraitAnimation = this.assetManager.getMovieClip(this.portraitProperties.animationProperties);
            this.portraitAnimation.onComplete = this.handleBackgroundComplete.bind(this);
            this.addChild(this.portraitAnimation);
        }
    }
    playAnimation(numberOfSlingos) {
        if (this.hasPortraitProperties && this.renderer.isPortrait()) {
            this.portraitAnimation.gotoAndPlay(0);
        }
        else {
            this.landscapeAnimation.gotoAndPlay(0);
        }
    }
    handleRotate(event) {
        super.handleRotate(event);
        if (this.hasPortraitProperties) {
            const data = event?.getCurrentTarget() || {};
            const landscape = this.renderer.isLandscape(data.forceOrientation);
            this.landscapeAnimation.renderable = landscape;
            this.portraitAnimation.renderable = !landscape;
        }
    }
}
/**
 * SlingoAnimationSpine.ts
 */
export class SlingoAnimationSpine extends AbstractSlingoAnimation {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    createAnimation() {
        this.animation = new TitanSpine(this.landscapeProperties.animationProperties, this.portraitProperties?.animationProperties);
        this.addChild(this.animation);
    }
    reset() {
        super.reset();
        if (this.animation) {
            this.removeChild(this.animation);
            this.animation = null;
        }
    }
    playAnimation(numberOfSlingos) {
        if (!this.animation) {
            this.createAnimation();
        }
        SpineUtil.play(this.animation, this.getAnimation(numberOfSlingos), this.handleBackgroundComplete, this);
    }
    getAnimation(numberOfSlingos) {
        let animation;
        if (this.uiProperties.animationNameArray && this.uiProperties.animationNameArray.length) {
            animation = this.uiProperties.animationNameArray[numberOfSlingos];
        }
        else if (this.uiProperties.hasMultiple) {
            animation = numberOfSlingos > 1 ? this.uiProperties.multiAnimationName : this.uiProperties.singleAnimationName;
        }
        else {
            animation = this.uiProperties.singleAnimationName;
        }
        return animation;
    }
    addTextAnimation(numberOfSlingos) {
        if (this.uiProperties.animationNameArray && this.uiProperties.animationNameArray.length) {
            return;
        }
        super.addTextAnimation(numberOfSlingos);
    }
}
//# sourceMappingURL=SlingoAnimation.js.map